import { useState, useEffect, useCallback, useMemo } from "react";
import { useCronograma } from "./useCronograma";

/**
 * Interface for a single conformity analysis item
 */
export interface AnaliseConformidadeItem {
  id: string;
  item: string;
  exigencia: string;
  implementado: string;
  status:
    | "conforme"
    | "nao_conforme"
    | "atrasado"
    | "pendente"
    | "em_andamento";
  artigo: string;
  faseId?: string;
  dataLimite?: Date;
  dataConclusao?: Date;
  diasAtraso?: number;
}

/**
 * Interface for conformity analysis summary
 */
export interface ResumoConformidade {
  totalItens: number;
  conformes: number;
  naoConformes: number;
  atrasados: number;
  pendentes: number;
  emAndamento: number;
  percentualConformidade: number;
  alertaCritico: boolean;
}

/**
 * Mapping between phase IDs and NR-05 articles
 */
const MAPEAMENTO_NR05: Record<string, { exigencia: string; artigo: string }> = {
  "comissao-eleitoral": {
    exigencia: "Mínimo 60 dias antes do término do mandato",
    artigo: "Art. 5.5.2",
  },
  "comunicacao-sindicato": {
    exigencia: "Com antecedência, após formação da comissão",
    artigo: "Art. 5.5.1.1",
  },
  "publicacao-edital": {
    exigencia: "Abertura de prazos para inscrições",
    artigo: "Art. 5.5.3(a)",
  },
  inscricoes: {
    exigencia: "Mínimo 15 dias corridos",
    artigo: "Art. 5.5.3(b)",
  },
  "lista-candidatos": {
    exigencia: "Após término das inscrições",
    artigo: "Art. 5.5.3(e)",
  },
  votacao: {
    exigencia: "Mínimo 30 dias antes do término do mandato",
    artigo: "Art. 5.5.3(f)",
  },
  eleicao: {
    exigencia: "Mínimo 30 dias antes do término do mandato",
    artigo: "Art. 5.5.3(f)",
  },
  apuracao: {
    exigencia: "Em horário normal de trabalho",
    artigo: "Art. 5.5.3(i)",
  },
  treinamento: {
    exigencia: "Antes da posse, carga horária conforme NR",
    artigo: "Art. 5.7.1",
  },
  posse: {
    exigencia: "Primeiro dia útil após término do mandato anterior",
    artigo: "Art. 5.4.7",
  },
  "ata-instalacao": {
    exigencia: "Registro da primeira reunião",
    artigo: "Art. 5.4.5",
  },
  "registro-orgaos": {
    exigencia: "Registro em órgãos competentes",
    artigo: "Art. 5.4.8",
  },
};

/**
 * Hook para análise de conformidade dinâmica baseada no cronograma real
 *
 * @returns Dados de conformidade calculados dinamicamente
 */
export const useConformidade = () => {
  const { fases, loading: loadingFases, error: errorFases } = useCronograma();

  const [analiseConformidade, setAnaliseConformidade] = useState<
    AnaliseConformidadeItem[]
  >([]);
  const [resumo, setResumo] = useState<ResumoConformidade>({
    totalItens: 0,
    conformes: 0,
    naoConformes: 0,
    atrasados: 0,
    pendentes: 0,
    emAndamento: 0,
    percentualConformidade: 0,
    alertaCritico: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Calculates the status of a phase based on dates and completion
   */
  const calcularStatusFase = useCallback(
    (
      fase: any,
    ):
      | "conforme"
      | "nao_conforme"
      | "atrasado"
      | "pendente"
      | "em_andamento" => {
      const agora = new Date();
      const dataLimite = new Date(fase.data);
      const dataConclusao = fase.data_conclusao
        ? new Date(fase.data_conclusao)
        : null;

      // Se a fase foi concluída
      if (fase.concluida || fase.status === "concluida") {
        // Verificar se foi concluída dentro do prazo
        if (dataConclusao && dataConclusao <= dataLimite) {
          return "conforme";
        }
        // Concluída, mas atrasada
        return "nao_conforme";
      }

      // Se a data limite já passou e não foi concluída
      if (agora > dataLimite) {
        return "atrasado";
      }

      // Se está dentro dos próximos 7 dias
      const diasParaVencer = Math.floor(
        (dataLimite.getTime() - agora.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (diasParaVencer <= 7) {
        return "em_andamento";
      }

      return "pendente";
    },
    [],
  );

  /**
   * Generates a description of implementation based on phase data
   */
  const gerarDescricaoImplementacao = useCallback(
    (fase: any, status: string): string => {
      const dataLimite = new Date(fase.data);
      const dataFormatada = dataLimite.toLocaleDateString("pt-BR");

      if (fase.data_conclusao) {
        const dataConclusao = new Date(fase.data_conclusao);
        return `Concluído em ${dataConclusao.toLocaleDateString("pt-BR")}`;
      }

      switch (status) {
        case "conforme":
          return `Planejado para ${dataFormatada} (conforme NR)`;
        case "nao_conforme":
          return `Vencido em ${dataFormatada} - AÇÃO NECESSÁRIA`;
        case "atrasado":
          const diasAtraso = Math.floor(
            (new Date().getTime() - dataLimite.getTime()) /
              (1000 * 60 * 60 * 24),
          );
          return `Atrasado ${diasAtraso} dias (venceu em ${dataFormatada})`;
        case "em_andamento":
          const diasRestantes = Math.floor(
            (dataLimite.getTime() - new Date().getTime()) /
              (1000 * 60 * 60 * 24),
          );
          return `${diasRestantes} dias restantes (vence em ${dataFormatada})`;
        case "pendente":
          return `Planejado para ${dataFormatada}`;
        default:
          return dataFormatada;
      }
    },
    [],
  );

  /**
   * Calculates conformity analysis based on current phases
   */
  const calcularConformidade = useCallback(() => {
    if (!fases || fases.length === 0) {
      setAnaliseConformidade([]);
      return;
    }

    const analise: AnaliseConformidadeItem[] = fases.map((fase) => {
      // Use the phase ID for NR-05 mapping
      const faseId = fase.id || "";
      const mapeamento = MAPEAMENTO_NR05[faseId] || {
        exigencia: fase.prazoNR || "Conforme cronograma",
        artigo: fase.artigo || "-",
      };

      const status = calcularStatusFase(fase);
      const dataLimite = new Date(fase.data);
      const agora = new Date();
      const diasAtraso =
        status === "atrasado"
          ? Math.floor(
              (agora.getTime() - dataLimite.getTime()) / (1000 * 60 * 60 * 24),
            )
          : undefined;

      // Get completion date from acoes_banco if available
      const dataConclusaoStr = fase.acoes_banco?.find(
        (a) => a.concluida,
      )?.data_conclusao;

      return {
        id: fase.id || `fase-${faseId}`,
        item: fase.titulo,
        exigencia: mapeamento.exigencia,
        implementado: gerarDescricaoImplementacao(fase, status),
        status,
        artigo: mapeamento.artigo,
        faseId,
        dataLimite,
        dataConclusao: dataConclusaoStr
          ? new Date(dataConclusaoStr)
          : undefined,
        diasAtraso,
      };
    });

    setAnaliseConformidade(analise);

    // Calculate summary
    const conformes = analise.filter((a) => a.status === "conforme").length;
    const naoConformes = analise.filter(
      (a) => a.status === "nao_conforme",
    ).length;
    const atrasados = analise.filter((a) => a.status === "atrasado").length;
    const pendentes = analise.filter((a) => a.status === "pendente").length;
    const emAndamento = analise.filter(
      (a) => a.status === "em_andamento",
    ).length;
    const totalItens = analise.length;

    // Calculate conformity percentage (conformes / (conformes + não conformes + atrasados))
    const itensAvaliados = conformes + naoConformes + atrasados;
    const percentualConformidade =
      itensAvaliados > 0 ? Math.round((conformes / itensAvaliados) * 100) : 100;

    // Critical alert if there are overdue items or conformity is below 80%
    const alertaCritico =
      atrasados > 0 || naoConformes > 0 || percentualConformidade < 80;

    setResumo({
      totalItens,
      conformes,
      naoConformes,
      atrasados,
      pendentes,
      emAndamento,
      percentualConformidade,
      alertaCritico,
    });
  }, [fases, calcularStatusFase, gerarDescricaoImplementacao]);

  // Recalculate when phases change
  useEffect(() => {
    if (!loadingFases) {
      setLoading(true);
      try {
        calcularConformidade();
        setError(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Erro ao calcular conformidade",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [fases, loadingFases, calcularConformidade]);

  // Handle error from useCronograma
  useEffect(() => {
    if (errorFases) {
      setError(errorFases);
    }
  }, [errorFases]);

  /**
   * Gets the color class for a status badge
   */
  const getStatusColor = useCallback(
    (status: AnaliseConformidadeItem["status"]): string => {
      switch (status) {
        case "conforme":
          return "bg-green-100 text-green-800";
        case "nao_conforme":
          return "bg-red-100 text-red-800";
        case "atrasado":
          return "bg-red-100 text-red-800";
        case "em_andamento":
          return "bg-yellow-100 text-yellow-800";
        case "pendente":
          return "bg-gray-100 text-gray-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    },
    [],
  );

  /**
   * Gets the label for a status
   */
  const getStatusLabel = useCallback(
    (status: AnaliseConformidadeItem["status"]): string => {
      switch (status) {
        case "conforme":
          return "CONFORME";
        case "nao_conforme":
          return "NÃO CONFORME";
        case "atrasado":
          return "ATRASADO";
        case "em_andamento":
          return "EM ANDAMENTO";
        case "pendente":
          return "PENDENTE";
        default:
          // Fallback for any unexpected value
          return String(status).toUpperCase();
      }
    },
    [],
  );

  /**
   * Filter items by status
   */
  const filtrarPorStatus = useCallback(
    (status: AnaliseConformidadeItem["status"]) => {
      return analiseConformidade.filter((item) => item.status === status);
    },
    [analiseConformidade],
  );

  /**
   * Get critical items (overdue or non-conforming)
   */
  const itensCriticos = useMemo(() => {
    return analiseConformidade.filter(
      (item) => item.status === "atrasado" || item.status === "nao_conforme",
    );
  }, [analiseConformidade]);

  /**
   * Get items due soon (next 7 days)
   */
  const itensProximosVencer = useMemo(() => {
    return analiseConformidade.filter((item) => item.status === "em_andamento");
  }, [analiseConformidade]);

  return {
    analiseConformidade,
    resumo,
    loading: loading || loadingFases,
    error,
    getStatusColor,
    getStatusLabel,
    filtrarPorStatus,
    itensCriticos,
    itensProximosVencer,
    recalcular: calcularConformidade,
  };
};
